import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Button } from 'src/components/system/Button';
import { TimerContext } from 'src/hooks/useTimer';
import { finishSimulation, getSimulation, sendAnswer } from 'src/services/simulations';
import { useCourseCategories } from 'src/contexts/CourseCategoriesContext';
import { toast } from 'react-toastify';
import { formatSimulationStatus } from 'src/utils/simulationStatus';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 32px 24px;
`;

const Box = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
`;

type Answer = { id: number; text: string; img?: string };
type Question = {
  id: number;
  code: string;
  statement: string;
  answers: Answer[];
  answered?: number | null;
};

type Simulation = {
  id: number;
  size: number;
  status: 'ongoing' | 'finished';
  categoryId?: number | null;
  categoryName?: string | null;
  random?: boolean;
  questions: Question[];
};

export function Simulator() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { time, setRunning, setTime } = useContext(TimerContext);
  const { categories } = useCourseCategories();

  const [simulation, setSimulation] = useState<Simulation | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(true);
  const [finishing, setFinishing] = useState(false);

  useEffect(() => {
    if (!sessionId) return;

    (async () => {
      setLoading(true);

      try {
        const data = await getSimulation(sessionId);

        if (data.status === 'finished') {
          navigate(`/results/${data.id}`);
          return;
        }

        const mappedQuestions: Question[] = (data.questions || []).map(question => ({
          id: question.id,
          code: question.code,
          statement: question.statement,
          answers: question.answers,
          answered: question.answered ?? null,
        }));

        const mappedSimulation: Simulation = {
          id: data.id,
          size: data.size,
          status: data.status as Simulation['status'],
          categoryId: (data as any).category_id ?? null,
          categoryName: (data as any).category_name ?? null,
          random: Boolean((data as any).random ?? true),
          questions: mappedQuestions,
        };

        setSimulation(mappedSimulation);
        setQuestions(mappedQuestions);
        setAnswers(
          Object.fromEntries(
            mappedQuestions
              .filter(q => typeof q.answered === 'number')
              .map(q => [q.id, q.answered as number]),
          ),
        );

        document.title = `Simulado | Simulado #${data.id}`;
        setTime(0);
        setRunning(true);
      } catch (error: any) {
        if (error?.response?.status === 401) {
          navigate('/auth/login');
          return;
        }
        toast.error('Não foi possível carregar o simulado.');
        setSimulation(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, sessionId, setRunning, setTime]);

  const canFinish = useMemo(() => simulation?.status === 'ongoing', [simulation]);

  function handleSaveAndExit() {
    setRunning(false);
    navigate('/history');
  }

  async function handleFinish() {
    if (!sessionId || !simulation) return;

    const simIdNum = Number(sessionId);
    if (Number.isNaN(simIdNum)) {
      const fallbackFamilySlug = categories[0]?.slug ?? 'cfp';
      navigate(`/families/${fallbackFamilySlug}`);
      return;
    }

    setFinishing(true);
    setRunning(false);

    try {
      await finishSimulation(simIdNum);
      navigate(`/results/${simIdNum}`);
    } catch (error: any) {
      if (error?.response?.status === 401) {
        navigate('/auth/login');
        return;
      }
      toast.error('Não foi possível finalizar o simulado.');
      setRunning(true);
    } finally {
      setFinishing(false);
    }
  }

  async function handleAnswerSelection(questionId: number, answerId: number) {
    if (!sessionId) return;

    setAnswers(prev => ({ ...prev, [questionId]: answerId }));
    setQuestions(prev =>
      prev.map(question =>
        question.id === questionId
          ? {
              ...question,
              answered: answerId,
            }
          : question,
      ),
    );
    setSimulation(prev =>
      prev
        ? {
            ...prev,
            questions: prev.questions.map(question =>
              question.id === questionId
                ? {
                    ...question,
                    answered: answerId,
                  }
                : question,
            ),
          }
        : prev,
    );

    try {
      await sendAnswer(Number(sessionId), questionId, answerId, time);
    } catch (error: any) {
      if (error?.response?.status === 401) {
        navigate('/auth/login');
        return;
      }
      toast.error('Não foi possível salvar a resposta. Tente novamente.');
    }
  }

  if (loading) {
    return <Page>Carregando simulado...</Page>;
  }

  if (!simulation) {
    return <Page>Simulado não encontrado.</Page>;
  }

  const currentQuestion = questions[current];
  const sessionTitle = simulation.categoryName ? simulation.categoryName : `Simulado #${simulation.id}`;

  return (
    <Page>
      <h1>{sessionTitle}</h1>
      <Box>
        <p>Tamanho: {simulation.size} questões</p>
        <p>Modo: {simulation.random ? 'Aleatório' : 'Personalizado'}</p>
        <p>Status: {formatSimulationStatus(simulation.status)}</p>
        <p>
          Tempo: {('0' + parseInt(String((time / 3600) % 3600))).slice(-2)}:
          {('0' + parseInt(String((time / 60) % 60))).slice(-2)}:
          {('0' + (time % 60)).slice(-2)}
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            width="160px"
            backgroundColor="#ffffff"
            buttonColor="#222B82"
            borderColor="#222B82"
            onClick={() => setRunning(false)}
            label={<span>Pausar</span>}
          />
          <Button
            width="160px"
            backgroundColor="#222B82"
            borderColor="#222B82"
            buttonColor="#ffffff"
            onClick={() => setRunning(true)}
            label={<span>Retomar</span>}
          />
          <Button
            width="160px"
            backgroundColor="#ffffff"
            buttonColor="#222B82"
            borderColor="#222B82"
            onClick={() => setTime(0)}
            label={<span>Zerar</span>}
          />
        </div>
      </Box>
      <Box>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {questions.map((question, idx) => {
            const answered = answers[question.id];
            const active = current === idx;
            return (
              <button
                key={question.id}
                onClick={() => setCurrent(idx)}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 6,
                  border: `1px solid ${active ? '#222B82' : '#e0e0e0'}`,
                  background: answered ? '#EEF2FF' : '#fff',
                }}
              >
                {idx + 1}
              </button>
            );
          })}
        </div>
      </Box>
      <Box>
        {currentQuestion && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <strong>
                Questão ({currentQuestion.code}) {current + 1}/{questions.length}
              </strong>
            </div>
            <div>{currentQuestion.statement}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {currentQuestion.answers.map(answer => {
                const checked = answers[currentQuestion.id] === answer.id;
                return (
                  <label
                    key={answer.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '12px 10px',
                      background: checked ? '#F1FAF3' : '#F7F7F7',
                      borderRadius: 8,
                    }}
                  >
                    <input
                      type="radio"
                      name={`q-${currentQuestion.id}`}
                      checked={checked}
                      onChange={() => handleAnswerSelection(currentQuestion.id, answer.id)}
                    />
                    {answer.text}
                  </label>
                );
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                width="160px"
                backgroundColor="#ffffff"
                buttonColor="#222B82"
                borderColor="#222B82"
                onClick={() => setCurrent(Math.max(0, current - 1))}
                label={<span>Anterior</span>}
              />
              <Button
                width="160px"
                backgroundColor="#222B82"
                borderColor="#222B82"
                buttonColor="#ffffff"
                onClick={() => setCurrent(Math.min(questions.length - 1, current + 1))}
                label={<span>Próximo</span>}
              />
            </div>
          </div>
        )}
      </Box>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Button
          width="220px"
          backgroundColor="#ffffff"
          buttonColor="#222B82"
          borderColor="#222B82"
          onClick={handleSaveAndExit}
          label={<span>Salvar e sair</span>}
        />
        <Button
          width="220px"
          backgroundColor="#D81E5B"
          borderColor="#D81E5B"
          buttonColor="#ffffff"
          disabled={!canFinish || finishing}
          onClick={canFinish ? handleFinish : undefined}
          label={<span>{finishing ? 'Finalizando...' : 'Finalizar simulado'}</span>}
        />
      </div>
    </Page>
  );
}
